<template>
  <!-- Reviews landing page: cards that open bottom sheet menus -->
  <v-container fluid :class="mobile ? 'pa-2' : 'pa-4'" class="fill-height align-start">
    <v-row class="justify-center">
      <v-col cols="12" sm="10" md="8">
        <!-- Welcome title -->
        <h2 class="text-h5 text-center mb-6 mt-4">Reviews</h2>

        <v-row>
          <!-- A) Reviews Pool -->
          <v-col cols="12" sm="6" class="d-flex">
            <v-card class="service-card flex-grow-1" variant="outlined" @click="openSheet('reviews')" hover>
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-star" size="32" color="amber" />
                </template>
                <v-card-title>Reviews</v-card-title>
                <v-card-subtitle class="text-wrap">Review pool and management</v-card-subtitle>
              </v-card-item>
              <v-card-text class="text-caption text-medium-emphasis pt-0">
                Browse and manage the review pool, import new reviews, and configure review settings.
              </v-card-text>
            </v-card>
          </v-col>

          <!-- B) Assignments -->
          <v-col cols="12" sm="6" class="d-flex">
            <v-card class="service-card flex-grow-1" variant="outlined" @click="openSheet('assignments')" hover>
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-clipboard-check-outline" size="32" color="indigo" />
                </template>
                <v-card-title>Assignments</v-card-title>
                <v-card-subtitle class="text-wrap">Scheduling and tracking</v-card-subtitle>
              </v-card-item>
              <v-card-text class="text-caption text-medium-emphasis pt-0">
                View today's review tasks, check the weekly calendar, manage assignment rules, and export schedule reports.
              </v-card-text>
            </v-card>
          </v-col>

          <!-- C) Costs -->
          <v-col cols="12" sm="6" class="d-flex">
            <v-card class="service-card flex-grow-1" variant="outlined" @click="openSheet('costs')" hover>
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-currency-usd" size="32" color="green" />
                </template>
                <v-card-title>Costs</v-card-title>
                <v-card-subtitle class="text-wrap">Review cost tracking</v-card-subtitle>
              </v-card-item>
              <v-card-text class="text-caption text-medium-emphasis pt-0">
                Generate cost reports by date range showing per-platform review expenses and totals.
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Bottom sheet for Reviews -->
    <v-bottom-sheet v-model="sheetReviews" inset>
      <v-list density="comfortable">
        <v-toolbar density="compact" color="transparent">
          <v-toolbar-title>Reviews</v-toolbar-title>
          <template v-slot:append>
            <v-btn icon size="large" @click="sheetReviews = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
        <v-divider />
        <!-- Reviews Management (admin) -->
        <v-list-item
          v-if="isAdmin"
          prepend-icon="mdi-database-edit-outline"
          title="Reviews Management"
          subtitle="Add, edit, and delete reviews"
          @click="go('/settings/reviews-admin')"
        />
        <!-- Bulk Import (admin) -->
        <v-list-item
          v-if="isAdmin"
          prepend-icon="mdi-file-upload"
          title="Import Reviews"
          subtitle="Bulk import from file"
          @click="go('/reviews-import')"
        />
        <!-- Review Settings (admin) -->
        <v-list-item
          v-if="isAdmin"
          prepend-icon="mdi-cog"
          title="Review Settings"
          subtitle="Cooldown and cost per platform"
          @click="sheetReviews = false; settingsDialogOpen = true"
        />
      </v-list>
    </v-bottom-sheet>

    <!-- Bottom sheet for Assignments -->
    <v-bottom-sheet v-model="sheetAssignments" inset>
      <v-list density="comfortable">
        <v-toolbar density="compact" color="transparent">
          <v-toolbar-title>Assignments</v-toolbar-title>
          <template v-slot:append>
            <v-btn icon size="large" @click="sheetAssignments = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
        <v-divider />
        <!-- Today's assignments -->
        <v-list-item
          prepend-icon="mdi-calendar-today"
          title="Today's Assignments"
          subtitle="View and complete today's reviews"
          @click="go('/reviews/today')"
        />
        <!-- Calendar -->
        <v-list-item
          prepend-icon="mdi-calendar-month"
          title="Assignment Calendar"
          subtitle="Weekly calendar view"
          @click="sheetAssignments = false; openCalendar()"
        />
        <!-- Manage assignments (admin) -->
        <v-list-item
          v-if="isAdmin"
          prepend-icon="mdi-table"
          title="Manage Assignments"
          subtitle="View and edit all assignment entries"
          @click="go('/review-assignments')"
        />
        <!-- Coverage Report -->
        <v-list-item
          prepend-icon="mdi-chart-box-outline"
          title="Coverage Report"
          subtitle="Check review coverage status"
          @click="sheetAssignments = false; openCoverageReport()"
        />
        <!-- Export Schedule PDF -->
        <v-list-item
          prepend-icon="mdi-file-pdf-box"
          title="Export Schedule PDF"
          subtitle="Current week schedule report"
          @click="sheetAssignments = false; onExportSchedulePdf()"
        />
      </v-list>
    </v-bottom-sheet>

    <!-- Bottom sheet for Costs -->
    <v-bottom-sheet v-model="sheetCosts" inset>
      <v-list density="comfortable">
        <v-toolbar density="compact" color="transparent">
          <v-toolbar-title>Costs</v-toolbar-title>
          <template v-slot:append>
            <v-btn icon size="large" @click="sheetCosts = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
        <v-divider />
        <!-- Set cost per platform (admin) -->
        <v-list-item
          v-if="isAdmin"
          prepend-icon="mdi-cog"
          title="Cost per Platform"
          subtitle="Set cost per review for each platform"
          @click="sheetCosts = false; costsDialogOpen = true"
        />
        <!-- Cost Report PDF -->
        <v-list-item
          prepend-icon="mdi-file-pdf-box"
          title="Cost Report PDF"
          subtitle="Platform costs for a date range"
          @click="sheetCosts = false; costReportDialogOpen = true"
        />
      </v-list>
    </v-bottom-sheet>

    <!-- Review Settings Dialog (full: cooldown + costs, from Reviews sheet) -->
    <ReviewSettingsDialog v-model="settingsDialogOpen" />

    <!-- Cost per Platform Dialog (costs only, no cooldown, from Costs sheet) -->
    <ReviewSettingsDialog v-model="costsDialogOpen" costs-only />

    <!-- Coverage Report Dialog -->
    <ReviewsCoverageReportDialog />

    <!-- Cost Report Date Range Dialog -->
    <BaseDialog
      v-model="costReportDialogOpen"
      title="Cost Report"
      title-icon="mdi-file-pdf-box"
      :max-width="400"
      save-text="Generate PDF"
      :save-loading="exportingCost"
      :save-disabled="!costDateFrom || !costDateTo"
      @save="onExportCostPdf"
    >
      <div class="text-caption text-medium-emphasis mb-3">Select the date range for the cost report</div>
      <DateRangeFilter
        v-model:from="costDateFrom"
        v-model:to="costDateTo"
        :clearable="false"
        stacked
        @change="({ from }) => onCostDateFromChange(from)"
      />
    </BaseDialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";
import { useReviews } from "@/composables/useReviews.js";
import api from "@/utils/api.js";
import { toLocalYMD, toLocalYM } from "@/utils/dateLocal.js";
import ReviewSettingsDialog from "@/components/ReviewSettingsDialog.vue";
import ReviewsCoverageReportDialog from "@/components/ReviewsCoverageReportDialog.vue";
import BaseDialog from "@/components/BaseDialog.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import { periodRange } from "@/utils/datePresets.js";

const router = useRouter();
const { mobile } = useDisplay();
const { user } = useApp();
const { openReviewsCoverageDialog } = useReviews();

// Role checks
const isAdmin = computed(() => ["admin", "appadmin"].includes(user.value?.role));

// Bottom sheet visibility refs
const sheetReviews = ref(false);
const sheetAssignments = ref(false);
const sheetCosts = ref(false);

/** Open the bottom sheet for a given card section. */
function openSheet(section) {
  if (section === "reviews") sheetReviews.value = true;
  else if (section === "assignments") sheetAssignments.value = true;
  else if (section === "costs") sheetCosts.value = true;
}

/** Navigate and close all sheets. */
function go(path) {
  sheetReviews.value = false;
  sheetAssignments.value = false;
  sheetCosts.value = false;
  router.push(path);
}

// Dialog refs
const settingsDialogOpen = ref(false);
const costsDialogOpen = ref(false);
const costReportDialogOpen = ref(false);

// Cost report date range (default: current month)
const now = new Date();
// Opens on this month, in Dubai days (utils/datePresets.js).
const [_costFrom, _costTo] = periodRange("thisMonth");
const costDateFrom = ref(_costFrom);
const costDateTo = ref(_costTo);

/** Auto-sync end date when start date changes (date auto-sync pattern). */
function onCostDateFromChange(val) {
  if (val && costDateFrom.value > costDateTo.value) {
    costDateTo.value = costDateFrom.value;
  }
}

// Loading states
const exportingSchedule = ref(false);
const exportingCost = ref(false);

// Snackbar
const snackbar = ref({ show: false, message: "", color: "success" });

/**
 * Open calendar view — new window on desktop, in-app on mobile.
 */
function openCalendar() {
  if (mobile.value) {
    router.push("/reviews-calendar");
  } else {
    const route = router.resolve("/reviews-calendar");
    window.open(route.href, "_blank");
  }
}

/**
 * Open coverage report dialog via the shared reviews composable.
 */
function openCoverageReport() {
  openReviewsCoverageDialog();
}

/**
 * Export schedule PDF (current week) — same logic as ReviewsMenu.vue.
 */
async function onExportSchedulePdf() {
  exportingSchedule.value = true;
  try {
    // Fetch assignments and users in parallel
    const [assignmentsRes, usersRes] = await Promise.all([
      api.marketingApi.get("/api/marketing/reviews/admin/assignments"),
      api.authApi.get("/api/admin/users"),
    ]);
    // Map fields for PDF report compatibility
    const entries = (assignmentsRes.data.entries || []).map((e) => ({
      ...e,
      schedule_date: e.assignment_date,
      assigned_user_id: e.user_id,
    }));
    const usersMap = new Map();
    for (const u of usersRes.data || []) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
      usersMap.set(u.id, { name: name || u.email, email: u.email });
    }
    const selectedMonth = toLocalYM();
    const { generateReviewCalendarPdf } = await import("@/utils/reviewCalendarPdfReport.js");
    const result = generateReviewCalendarPdf(entries, selectedMonth, usersMap);
    if (result?.error) {
      snackbar.value = { show: true, message: result.error, color: "warning" };
    }
  } catch (err) {
    console.error("Failed to export PDF:", err);
    snackbar.value = { show: true, message: "Failed to export PDF", color: "error" };
  } finally {
    exportingSchedule.value = false;
  }
}

/**
 * Export cost report PDF for the selected date range.
 * Fetches config + assignments, then generates PDF.
 */
async function onExportCostPdf() {
  exportingCost.value = true;
  try {
    // Fetch config and assignments in parallel
    const [configRes, assignmentsRes] = await Promise.all([
      api.marketingApi.get("/api/marketing/reviews/admin/config"),
      api.marketingApi.get("/api/marketing/reviews/admin/assignments"),
    ]);
    const config = configRes.data || {};
    const entries = assignmentsRes.data.entries || [];

    const { generateReviewCostReportPdf } = await import("@/utils/reviewCostReportPdf.js");
    const result = generateReviewCostReportPdf({
      config,
      entries,
      dateFrom: costDateFrom.value,
      dateTo: costDateTo.value,
    });
    if (result?.error) {
      snackbar.value = { show: true, message: result.error, color: "warning" };
    } else {
      costReportDialogOpen.value = false;
    }
  } catch (err) {
    console.error("Failed to export cost PDF:", err);
    snackbar.value = { show: true, message: "Failed to export cost report", color: "error" };
  } finally {
    exportingCost.value = false;
  }
}
</script>

<style scoped>
.service-card {
  cursor: pointer;
  transition: border-color 0.2s;
}
.service-card:hover {
  border-color: rgb(var(--v-theme-primary));
}
</style>
